import React, { useContext, useEffect, useMemo, useState } from 'react';
import { LangContext } from '../../contexts/langContext';
import { types_dict } from '../../static/types_dictionary';
import { nakshatra_yoni_dictionary, yoni_dictionary } from '../../static/jyotish_local';
import { getJyotish } from '../Edu/utilsJyotish';
import { drawProfile } from '../utilsDesign';
import { Iching } from './Iching';
import { Icon } from './Icon';
import { Preloader } from './kit';
import { PopoverH } from './TooltipH';

const FilterCount = ({ count, ready = true }) => (
  <div className={`x_choose_menu_counter${ready ? '' : ' x_choose_menu_counter--loading'}`}>
    {!ready && (
      <span className="x_choose_menu_counter_load" aria-hidden>
        <Preloader width={16} height={16} />
      </span>
    )}
    <div>{count}</div>
  </div>
);


const TYPE_CONFIG = [
  { type: 'R', bgClass: 'r' },
  { type: 'M', bgClass: 'm' },
  { type: 'P', bgClass: 'p' },
  { type: 'G', bgClass: 'g' },
  { type: 'MG', bgClass: 'mg' },
];

const PROFILE_CONFIG = [
  [1, 3], [1, 4], [2, 4], [2, 5], [3, 5], [3, 6],
  [4, 6], [4, 1], [5, 1], [5, 2], [6, 2], [6, 3],
];

const countByDesignType = (array, type) =>
  array.filter((item) => item?.avatar?.design?.type === type).length;

const countByDesignProfile = (array, profile) =>
  array.filter((item) => {
    const avatar = item?.avatar;
    const p = avatar?.design?.profile;
    return p && p[0] === profile[0] && p[1] === profile[1];
  }).length;

const getYoniIdForAvatar = (avatar) => {
  if (!avatar?.dob || !avatar?.aggr?.city?.location) return null;
  try {
    const result = getJyotish(avatar);
    const nakshatra = result?.kundli?.dasha?.birthNakshatra;
    if (!nakshatra || typeof nakshatra !== 'string') return null;
    const normalized = nakshatra.toLowerCase().replace(/\s+/g, '_');
    return nakshatra_yoni_dictionary[normalized]?.yoniId || null;
  } catch {
    return null;
  }
};

const getYoniDictEntry = (yoniId) =>
  yoni_dictionary[yoniId] ||
  Object.values(yoni_dictionary).find((y) => y.id === yoniId) ||
  null;

export const matchesStatsFilter = (filter, match) => {
  if (!filter || !match) return false;
  if (match.type && !match.profile) return filter.type === match.type;
  if (match.profile) {
    const profileMatch = filter.profile?.[0] === match.profile[0]
      && filter.profile?.[1] === match.profile[1];
    if (match.type) return filter.type === match.type && profileMatch;
    return profileMatch;
  }
  if (match.yoni) return filter.yoni === match.yoni;
  return false;
};

export const filterArrayByStats = (array, filter) => {
  if (!filter || !array) return array;
  return array.filter((item) => {
    if (!item) return false;
    const { avatar } = item;
    if (filter.type && avatar?.design?.type !== filter.type) return false;
    if (filter.profile) {
      const profile = avatar?.design?.profile;
      if (!profile
        || profile[0] !== filter.profile[0]
        || profile[1] !== filter.profile[1]) return false;
    }
    if (filter.yoni && getYoniIdForAvatar(avatar) !== filter.yoni) return false;
    return true;
  });
};

export const SocialFilters = ({
  mode = 'hd',
  type = 'types',
  array = [],
  onSelect,
  activeFilter,
  onClear,
  onToggleMode,
  ready = true,
}) => {
  if (mode === 'yoni') {
    return <SocialFiltersYoni array={array} onSelect={onSelect} activeFilter={activeFilter} onClear={onClear} ready={ready} />;
  }

  switch (type) {
    case 'types':
      return <SocialFiltersTypes array={array} onSelect={onSelect} activeFilter={activeFilter} onClear={onClear} onToggleMode={onToggleMode} ready={ready} />;
    case 'profiles':
      return <SocialFiltersProfiles array={array} onSelect={onSelect} activeFilter={activeFilter} onClear={onClear} onToggleMode={onToggleMode} ready={ready} />;
    default:
      return null;
  }
};

const filterArrayByType = (array, type) =>
  array.filter((item) => item?.avatar?.design?.type === type);

const SocialFiltersTypeProfiles = ({ type, array, onSelect, activeFilter, ready = true }) => {
  const items = useMemo(() => {
    const typeFiltered = filterArrayByType(array, type);
    return PROFILE_CONFIG
      .map((profile) => ({
        profile,
        count: countByDesignProfile(typeFiltered, profile),
        label: drawProfile(profile),
      }))
      .filter(({ count }) => count > 0);
  }, [array, type]);

  if (!items.length) return null;

  return (
    <div className="x_choose_list x_choose_list--profiles" onClick={(e) => e.stopPropagation()}>
      {items.map(({ profile, count, label }) => {
        const isActive = activeFilter?.type === type
          && activeFilter?.profile?.[0] === profile[0]
          && activeFilter?.profile?.[1] === profile[1];
        const isIdle = activeFilter?.type === type && activeFilter?.profile && !isActive;
        return (
          <div
            key={label}
            className={[
              'x_choose_menu_but',
              isActive && 'active',
              isIdle && 'idle',
            ].filter(Boolean).join(' ')}
            onClick={() => onSelect?.({ type, profile })}
          >
            <Iching profile={profile} size='sm' />
            <div>{label}</div>
            <FilterCount count={count} ready={ready} />
          </div>
        );
      })}
    </div>
  );
};

const SocialFiltersTypeButton = ({
  type,
  bgClass,
  count,
  isActive,
  isIdle,
  onSelect,
  array,
  activeFilter,
  classSuffix,
  isMobile,
  mobilePopoverType,
  setMobilePopoverType,
  ready = true,
}) => {
  const title = classSuffix.includes('mobile_vh_yes')
    ? (types_dict[type]?.titleXsPlural || type)
    : (types_dict[type]?.titlePlural || type);

  const typeFiltered = filterArrayByType(array, type);
  const hasProfileFilters = count > 0 && PROFILE_CONFIG.some(
    (profile) => countByDesignProfile(typeFiltered, profile) > 0
  );

  const handleClick = () => {
    if (count === 0) return;
    if (isMobile && isActive && hasProfileFilters) {
      setMobilePopoverType(type);
      return;
    }
    if (isMobile) {
      setMobilePopoverType(null);
    }
    onSelect?.({ type });
  };

  const button = (
    <div
      className={[
        `x_choose_menu_but ${classSuffix}`,
        count === 0 && 'x_choose_menu_but--0',
        isActive && 'active',
        isIdle && 'idle',
      ].filter(Boolean).join(' ')}
      onClick={handleClick}
    >
      <div className="t_icon t_icon--type"></div>
      <div>{title}</div>
      <FilterCount count={count} ready={ready} />
      {bgClass && <div className={`x_choose_section_bg ${bgClass}`}></div>}
      {!bgClass && <div className="x_choose_section_bg"></div>}
    </div>
  );

  if (!hasProfileFilters) return button;

  const popoverContent = (
    <SocialFiltersTypeProfiles
      type={type}
      array={array}
      onSelect={onSelect}
      activeFilter={activeFilter}
      ready={ready}
    />
  );

  if (isMobile) {
    return (
      <PopoverH
        popupClassName="x_choose_list_profiles"
        placement="bottom"
        trigger={[]}
        open={mobilePopoverType === type}
        onOpenChange={(open) => {
          if (!open) setMobilePopoverType(null);
        }}
        title={popoverContent}
      >
        {button}
      </PopoverH>
    );
  }

  return (
    <PopoverH
      popupClassName="x_choose_list_profiles"
      placement="bottom"
      title={popoverContent}
    >
      {button}
    </PopoverH>
  );
};

export const SocialFiltersTypes = ({ array = [], onSelect, activeFilter, onClear, onToggleMode, ready = true }) => {
  const [mobilePopoverType, setMobilePopoverType] = useState(null);

  useEffect(() => {
    setMobilePopoverType(null);
  }, [activeFilter]);

  const counts = useMemo(
    () => TYPE_CONFIG.map(({ type }) => countByDesignType(array, type)),
    [array]
  );

  return (
    <div className="x_choose_list">
      {TYPE_CONFIG.map(({ type, bgClass }, index) => {
        const count = counts[index];
        const isActive = activeFilter?.type === type;
        const isIdle = activeFilter && !isActive;
        const buttonProps = {
          type,
          bgClass,
          count,
          isActive,
          isIdle,
          onSelect,
          array,
          activeFilter,
          mobilePopoverType,
          setMobilePopoverType,
          ready,
        };
        return (
          <div key={type}>
            <SocialFiltersTypeButton {...buttonProps} classSuffix="mobile_vh_no" isMobile={false} />
            <SocialFiltersTypeButton {...buttonProps} classSuffix="mobile_vh_yesf" isMobile />
          </div>
        );
      })}
      {activeFilter && (
        <div className="x_choose_menu_but x_choose_menu_but--blue" onClick={onClear}>
          <Icon>clear</Icon>
        </div>
      )}
      {!activeFilter && onToggleMode && (
        <div className="x_choose_menu_but" onClick={onToggleMode}>
          <Icon>refresh</Icon>
        </div>
      )}
    </div>
  );
};

export const SocialFiltersProfiles = ({ array = [], onSelect, activeFilter, onClear, onToggleMode, ready = true }) => {
  const counts = useMemo(
    () => PROFILE_CONFIG.map((profile) => countByDesignProfile(array, profile)),
    [array]
  );

  return (
    <div className="x_choose_list">
      {PROFILE_CONFIG.map((profile, index) => {
        const count = counts[index];
        if (count === 0) return null;
        const label = drawProfile(profile);
        const isActive = activeFilter?.profile
          && activeFilter.profile[0] === profile[0]
          && activeFilter.profile[1] === profile[1];
        const isIdle = activeFilter && !isActive;
        return (
          <div
            key={label}
            className={[
              'x_choose_menu_but',
              isActive && 'active',
              isIdle && 'idle',
            ].filter(Boolean).join(' ')}
            onClick={() => onSelect?.({ profile })}
          >
            <Iching profile={profile} size='sm'/>
            <div>{label}</div>
            <FilterCount count={count} ready={ready} />
          </div>
        );
      })}
      {activeFilter && (
        <div className="x_choose_menu_but x_choose_menu_but--blue" onClick={onClear}>
          <Icon>clear</Icon>
        </div>
      )}
      {!activeFilter && onToggleMode && (
        <div className="x_choose_menu_but" onClick={onToggleMode}>
          <Icon>refresh</Icon>
        </div>
      )}
    </div>
  );
};

export const SocialFiltersYoni = ({ array = [], onSelect, activeFilter, onClear, ready = true }) => {
  const { language = 'en' } = useContext(LangContext) || {};

  // Compute yoni counts once per array. Order follows yoni_dictionary's
  // natural insertion order so the buttons stay stable across renders.
  const buckets = useMemo(() => {
    const counts = {};
    array.forEach((item) => {
      const yoniId = getYoniIdForAvatar(item?.avatar);
      if (yoniId) counts[yoniId] = (counts[yoniId] || 0) + 1;
    });
    const seen = new Set();
    const items = [];
    Object.values(yoni_dictionary).forEach((entry) => {
      const id = entry.id || null;
      if (!id || seen.has(id)) return;
      seen.add(id);
      const count = counts[id] || 0;
      if (count > 0) items.push({ id, entry, count });
    });
    return items;
  }, [array]);

  if (!buckets.length) return null;

  return (
    <div className="x_choose_list">
      {buckets.map(({ id, entry, count }) => {
        const title =
          entry[`title-${language}`] || entry['title-en'] || id;
        const isActive = activeFilter?.yoni === id;
        const isIdle = activeFilter && !isActive;
        return (
          <div
            key={id}
            className={[
              'x_choose_menu_but',
              '_fgap04',
              isActive && 'active',
              isIdle && 'idle',
            ].filter(Boolean).join(' ')}
            onClick={() => count > 0 && onSelect?.({ yoni: id })}
          >
            <div className="x_choose_menu_yoni_emoji">{entry.emoji || '–'}</div>
            {/* <div>{title}</div> */}
            <FilterCount count={count} ready={ready} />
          </div>
        );
      })}
      {activeFilter && (
        <div className="x_choose_menu_but x_choose_menu_but--blue" onClick={onClear}>
          <Icon>clear</Icon>
        </div>
      )}
    </div>
  );
};
